"use client";

import { useState, useEffect } from "react";
import { X, Megaphone } from "lucide-react";
import { useAuth } from "../lib/auth";

const STORAGE_KEY = "fulkit.announcements.seen";

function getSeenIds() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function markSeen(id) {
  const seen = getSeenIds();
  if (!seen.includes(id)) {
    seen.push(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
  }
}

export default function AnnouncementBanner() {
  const { isOnboarded, profile } = useAuth();
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    // Don't show announcements until onboarded + 48h grace period
    if (!isOnboarded) return;
    const createdAt = profile?.created_at ? new Date(profile.created_at).getTime() : 0;
    const gracePeriod = 48 * 60 * 60 * 1000;
    if (createdAt && Date.now() - createdAt < gracePeriod) return;

    fetch("/api/broadcasts/active")
      .then(r => r.ok ? r.json() : [])
      .then(items => {
        const seen = getSeenIds();
        const unseen = items.filter(a => !seen.includes(a.id));
        if (unseen.length > 0) setAnnouncement(unseen[0]);
      })
      .catch(() => {});
  }, [isOnboarded, profile?.created_at]);

  const dismiss = () => {
    if (announcement) markSeen(announcement.id);
    setAnnouncement(null);
  };

  if (!announcement) return null;

  // 2026-05-09 — refactored from `position: fixed; left:0; right:0` (which
  // overlaid the full viewport including under the sidebar, making the
  // inner-content `maxWidth: 600 margin: 0 auto` center on viewport-middle
  // instead of content-column-middle — visible misalignment with page
  // content). Now in-flow inside AppShell content column (mounted in
  // components/AppShell.js right after BannerSlot). Same canonical-position
  // pattern as BannerSlot from Cluster B consolidation.
  return (
    <div style={{
      background: "var(--color-bg-inverse)",
      color: "var(--color-text-inverse)",
      fontFamily: "var(--font-primary)",
      borderRadius: "var(--radius-md)",
      marginBottom: "var(--space-4)",
    }}>
      <div style={{
        padding: "var(--space-2-5) var(--space-4)",
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
      }}>
        <Megaphone size={14} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.7 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: "var(--font-size-xs)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: 1,
          }}>
            {announcement.title}
          </div>
          <div style={{
            fontSize: "var(--font-size-2xs)",
            opacity: 0.8,
            lineHeight: "var(--line-height-relaxed)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {announcement.content.split("\n")[0]}
          </div>
        </div>
        <button
          onClick={dismiss}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "var(--space-1)",
            display: "flex",
            color: "var(--color-text-inverse)",
            opacity: 0.6,
            flexShrink: 0,
          }}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
